import { notifyManager, type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import { type Column, firstCountsTimeColumn } from '@/entities/board'
import { patchTaskCache, type Task, taskKeys } from '@/entities/task'
import {
  addRunningToCache,
  optimisticRunning,
  removeRunningFromCache,
  timeKeys,
} from '@/entities/time-entry'
import { apiErrorText, apiPost, beginOptimistic } from '@/shared/api'
import { toast } from '@/shared/ui/toast'

// Лёгкая ссылка на задачу — мутациям нужны только id и boardId (для ключей кэша).
export interface TaskTarget {
  id: string
  boardId: string
}

const takeErrors: Record<string, string> = {
  TASK_NO_TIME_COLUMN: 'На доске нет колонки, считающей время — её добавляют в настройках доски.',
  TASK_NOT_FOUND: 'Задача удалена или недоступна.',
}

const releaseErrors: Record<string, string> = {
  TASK_NOT_IN_WORK: 'Задача уже не в работе.',
  TASK_NO_RETURN_COLUMN: 'Некуда вернуть: на доске нет колонки вне «в работе».',
  TASK_NOT_FOUND: 'Задача удалена или недоступна.',
}

function touchedKeys(task: TaskTarget) {
  return [
    taskKeys.detail(task.id),
    taskKeys.board(task.boardId),
    taskKeys.mine(),
    timeKeys.running(),
  ]
}

// Заголовок задачи из кэша — таймер-бар показывает его сразу, до ответа сервера.
function titleOf(qc: QueryClient, task: TaskTarget): string {
  const detail = qc.getQueryData<Task>(taskKeys.detail(task.id))
  if (detail?.title) return detail.title
  const board = qc.getQueryData<Task[]>(taskKeys.board(task.boardId))
  return board?.find((t) => t.id === task.id)?.title ?? ''
}

// После take/release таймеры и роллап меняются на бэке — освежаем их и списки задач.
function invalidateAfter(qc: QueryClient, task: TaskTarget) {
  for (const key of touchedKeys(task)) void qc.invalidateQueries({ queryKey: key })
  void qc.invalidateQueries({ queryKey: timeKeys.rollup(task.id) })
  void qc.invalidateQueries({ queryKey: timeKeys.entries(task.id) })
}

export interface TakeVars {
  // stopOthers — закрыть остальные свои идущие задачи и вернуть их на места.
  // Решает человек в диалоге: вести несколько задач разом легально.
  stopOthers?: boolean
}

// Взять в работу: self-assign (в том числе перехват у прежнего исполнителя) +
// перенос в первую counts_time-колонку + старт таймера (POST /tasks/{id}/take).
// Оптимистично двигаем колонку, исполнителя и сам таймер — иначе карточка уже в
// «В работе», а футер ещё показывает Σ, и вид перемигивает, когда приходит ответ.
export function useTakeTask(task: TaskTarget, columns: Column[], meId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ stopOthers = false }: TakeVars = {}) =>
      apiPost(`/tasks/${task.id}/take`, { stop_others: stopOthers }),
    onMutate: async ({ stopOthers = false }: TakeVars = {}): Promise<{ rollback?: () => void }> => {
      const target = firstCountsTimeColumn(columns)
      if (!target) return {}
      const rollback = await beginOptimistic(qc, touchedKeys(task))
      // Один кадр: карточка переезжает и таймер зажигается вместе.
      notifyManager.batch(() => {
        patchTaskCache(qc, task.id, {
          columnId: target.id,
          ...(meId ? { assigneeId: meId, activeEmployeeId: meId } : {}),
        })
        // Прежние гаснут сразу — иначе бар ещё секунду показывает «+2», которых уже нет.
        if (stopOthers) qc.setQueryData(timeKeys.running(), [])
        addRunningToCache(
          qc,
          optimisticRunning({
            id: task.id,
            title: titleOf(qc, task),
            boardId: task.boardId,
            columnId: target.id,
          }),
        )
      })
      return { rollback }
    },
    onError: (err, _vars, ctx) => {
      ctx?.rollback?.()
      toast.error(apiErrorText(err, takeErrors, 'Не удалось взять в работу'))
    },
    // stopOthers трогает и другие задачи — их колонки и роллапы перечитываем целиком.
    onSettled: (_res, _err, vars) => {
      invalidateAfter(qc, task)
      if (vars?.stopOthers) {
        void qc.invalidateQueries({ queryKey: taskKeys.all })
        void qc.invalidateQueries({ queryKey: timeKeys.all })
      }
    },
  })
}

// «Остановить все» из таймер-бара: отпускаем каждую идущую задачу. allSettled —
// одна упавшая (её уже отпустили с другого устройства) не должна ронять остальные;
// расхождение снимет общий рефетч в onSettled.
export function useReleaseAll(tasks: TaskTarget[]) {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: async () => {
      await Promise.allSettled(tasks.map((t) => apiPost(`/tasks/${t.id}/release`)))
    },
    onMutate: async () => {
      await beginOptimistic(qc, [timeKeys.running()])
      qc.setQueryData(timeKeys.running(), [])
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.all })
      void qc.invalidateQueries({ queryKey: timeKeys.all })
    },
  })
  return () => mutation.mutate()
}

// Отпустить: POST /tasks/{id}/release — бэк гасит интервал и возвращает задачу в
// колонку, из которой её взяли (или в запасную, если той уже нет). Колонку
// оптимистично не двигаем: куда именно вернётся задача, знает только сервер —
// гасим лишь живой тик, иначе он идёт на уже отпущенной карточке.
export function useReleaseTask(task: TaskTarget) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiPost(`/tasks/${task.id}/release`),
    onMutate: async (): Promise<{ rollback?: () => void }> => {
      const rollback = await beginOptimistic(qc, touchedKeys(task))
      notifyManager.batch(() => {
        removeRunningFromCache(qc, task.id)
        patchTaskCache(qc, task.id, { activeEmployeeId: null })
      })
      return { rollback }
    },
    onError: (err, _vars, ctx) => {
      ctx?.rollback?.()
      toast.error(apiErrorText(err, releaseErrors, 'Не удалось отпустить'))
    },
    onSettled: () => invalidateAfter(qc, task),
  })
}
